import { pollWhileVisible } from '../poll.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PublicMarket } from '../api.js';
import { publicMarketShared } from '../sharedLine.js';
import { assetName, bareText, parseAmount, type Asset } from '../assets.js';
import { useLocalMoney } from '../money.js';
import { normalizeDecimal } from '../numbers.js';
import { simpleBook } from '../simpleCap.js';
import { estimate, saveDraft, simpleOrder, type Draft, type Side } from '../ticket.js';
import { track } from '../track.js';
import { Mark, perCoin, quoteFor } from './Market.js';
import { SimpleTicket } from './SimpleTicket.js';

type MarketPair = PublicMarket['pairs'][number];
interface Level {
  price: string;
  remaining: string;
}

/**
 * The public figures for someone with no wallet yet: the same answer
 * xchangehq.com reads, asked for once a minute while the page is in view.
 * Null until it arrives; `failed` when it could not be read at all.
 */
export function useWelcomeMarket(): { market: PublicMarket | null; failed: boolean } {
  const [market, setMarket] = useState<PublicMarket | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let gone = false;
    const read = (): void => {
      if (document.visibilityState !== 'visible') return;
      publicMarketShared()
        .then((m) => {
          if (!gone) setMarket(m);
        })
        .catch(() => {
          if (!gone) setFailed(true);
        });
    };
    read();
    // The server keeps the figures for a minute; asking more often gains nothing.
    const stop = pollWhileVisible(read, 60_000);
    return () => {
      gone = true;
      stop();
    };
  }, []);
  return { market, failed };
}

/**
 * The headline over the ticket. One line for every pair, so it stays true as
 * pairs are added: what to do, and that doing it earns XGE.
 */
export function headlineFor(_pairName: string | null): string {
  return 'Start swapping now and get real rewards.';
}

/**
 * The Simple ticket on the welcome, working before any wallet: pick a pair
 * and a side, type an amount, and it answers from the public book at the best
 * price by someone else, after the fee, as the trading screen will. Its one
 * button makes the wallet, and keeps the ticket on this device so the trading
 * screen opens with it filled in. Nothing is asked of the server but the
 * figures the welcome already reads.
 */
export function WelcomeTicket({
  pairs,
  pair,
  onPair,
  draft,
  onStart,
}: {
  pairs: MarketPair[];
  pair: MarketPair;
  onPair: (name: string) => void;
  /** The ticket kept from an earlier visit to this screen, if any. */
  draft: Draft | null;
  onStart: () => void;
}) {
  const [side, setSide] = useState<Side>(draft?.side ?? 'buy');
  const [amount, setAmount] = useState(draft?.amount ?? '');
  const { currency, rates } = useLocalMoney();
  const base = pair.base as Asset;
  const levels = (side === 'buy' ? pair.asks : pair.bids) ?? [];
  const simple = useMemo(() => simpleBook(levels), [levels]);
  const want = useMemo(() => {
    try {
      return parseAmount(base, normalizeDecimal(amount));
    } catch {
      return null;
    }
  }, [amount, base]);
  const order = simpleOrder(want, simple);
  const est = useMemo(() => {
    try {
      return estimate({ pair, side, base: order.amount, price: order.price, balances: null });
    } catch {
      return null;
    }
  }, [pair, side, order.amount, order.price]);
  // Once a visit: the ticket answered. The pair and side only, never the amount.
  const tried = useRef(false);
  useEffect(() => {
    if (est === null || tried.current) return;
    tried.current = true;
    track('welcome_try', { pair: pair.name, side });
  }, [est, pair.name, side]);

  return (
    <>
      <div className="wt-pairs welcome-pairs" role="tablist" aria-label="Pairs">
        {pairs.map((p) => (
          <button
            key={p.name}
            role="tab"
            aria-selected={p.name === pair.name}
            onClick={() => {
              onPair(p.name);
              track('welcome_pair', { pair: p.name });
            }}
            data-testid={`welcome-pair-${p.name}`}
          >
            <Mark asset={p.base} size={16} /> {p.display}
          </button>
        ))}
      </div>
      <SimpleTicket
        pair={pair}
        side={side}
        onSide={setSide}
        amount={amount}
        onAmount={setAmount}
        simple={simple}
        est={est}
        capped={order.capped}
        currency={currency}
        rates={rates}
        toAdvanced={null}
        action={
          <div className="welcome-ticket-go">
            <button
              className="primary welcome-cta"
              onClick={() => {
                saveDraft({ pair: pair.name, side, amount });
                onStart();
              }}
              data-testid="create-wallet"
            >
              {est === null ? 'Make a wallet' : 'Make a wallet to place this'}
            </button>
            <p className="welcome-time">About a minute. Nothing is placed until you sign it.</p>
          </div>
        }
      />
    </>
  );
}

/**
 * The book as it is now, read-only, under the ticket: the open bids and
 * offers of the pair the ticket is on, from the same public figures. It is
 * the proof that the ticket's answer is real.
 */
export function WelcomeBook({ pair }: { pair: MarketPair }) {
  return (
    <section
      className="wt-card welcome-market"
      data-testid="welcome-market"
      aria-label="The book now"
    >
      <div className="wt-card-title">
        The book now
        <span className="wt-card-sub welcome-live">
          <span className="welcome-live-dot" aria-hidden="true" />
          Live
        </span>
      </div>
      <Book pair={pair} />
    </section>
  );
}

function Book({ pair }: { pair: MarketPair }) {
  const base = pair.base as Asset;
  const quote = pair.quote as Asset;
  const asks = pair.asks ?? [];
  const bids = pair.bids ?? [];
  // Each side's bars against its own largest level, so one big bid does not flatten the offers.
  const largest = (levels: Level[]): bigint =>
    levels.reduce((m, l) => (BigInt(l.remaining) > m ? BigInt(l.remaining) : m), 0n);
  const most = { asks: largest(asks), bids: largest(bids) };
  const row = (l: Level, kind: 'asks' | 'bids', i: number) => (
    <div className={`wt-level ${kind}`} key={`${kind}-${i}`}>
      <span
        className="wt-depth"
        style={{
          width: `${most[kind] === 0n ? 0 : Math.max(4, Number((BigInt(l.remaining) * 100n) / most[kind]))}%`,
        }}
      />
      <span className="wt-price">{perCoin(pair, l.price)}</span>
      <span className="wt-amt">{bareText(base, BigInt(l.remaining))}</span>
      <span className="wt-total">
        {bareText(quote, quoteFor(pair, BigInt(l.remaining), BigInt(l.price)))}
      </span>
    </div>
  );
  return (
    <div className="welcome-book" data-testid="welcome-book">
      <div className="wt-level head">
        <span className="wt-price">Each ({quote === 'USDC' ? '$' : assetName[quote]})</span>
        <span className="wt-amt">{assetName[base]}</span>
        <span className="wt-total">{assetName[quote]}</span>
      </div>
      <div className="wt-asks">
        {asks.length === 0 ? (
          <div className="wt-empty">Nobody is selling right now.</div>
        ) : (
          asks.map((l, i) => row(l, 'asks', i))
        )}
      </div>
      <div className="wt-spread">
        {pair.trades24h > 0
          ? `${pair.trades24h.toLocaleString('en-US')} ${pair.trades24h === 1 ? 'trade' : 'trades'} in the last day`
          : 'Bids buy, offers sell'}
      </div>
      <div className="wt-bids">
        {bids.length === 0 ? (
          <div className="wt-empty">Nobody is buying right now.</div>
        ) : (
          bids.map((l, i) => row(l, 'bids', i))
        )}
      </div>
    </div>
  );
}
